import { Button, Select, Sheet, StatusChip } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { AppError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { hintOf } from '@/lib/orders';
import { fetchDrawers, shiftKeys } from '@/lib/shifts';
import type { PosContext } from './posData';
import { setTerminalChoice } from './posData';
import './pos.css';

/**
 * Money goes through a cash drawer (ADR-024). A POS tablet acts for its own drawer; the owner in a
 * browser names the drawer-bound POS tablet whose drawer takes or gives back the money.
 */
export function moneyAtPayload(ctx: Pick<PosContext, 'principal' | 'terminal'>): {
  terminal_id?: string;
} {
  return ctx.principal === 'human' && ctx.terminal ? { terminal_id: ctx.terminal.id } : {};
}

/** The browser's terminal picker: POS tablets (orders and money belong to one), with the drawer
 * each one is linked to. Which one is chosen, or that none is, shows in words and a shape beside it. */
export function TerminalPicker({ ctx }: { ctx: PosContext }) {
  const drawers = useQuery({
    queryKey: shiftKeys.drawers(ctx.locationId),
    enabled: ctx.principal === 'human' && !!ctx.locationId,
    staleTime: 60_000,
    queryFn: () => fetchDrawers(ctx.locationId),
  });
  if (ctx.principal !== 'human') return null;
  const drawerName = (id: string | null) =>
    id ? ((drawers.data ?? []).find((d) => d.id === id)?.name ?? 'drawer') : null;
  return (
    <div className="pos-terminal" data-state={ctx.terminal ? 'chosen' : 'none'}>
      {ctx.terminal ? (
        <StatusChip tone="success" testId="pos-terminal-state">
          Selling on {ctx.terminal.code}
        </StatusChip>
      ) : (
        <StatusChip tone="warning" testId="pos-terminal-state">
          No terminal chosen
        </StatusChip>
      )}
      <Select
        className="pos-terminal__select"
        value={ctx.terminal?.id ?? ''}
        onChange={(e) => setTerminalChoice(ctx.locationId, e.target.value || null)}
        aria-label="Terminal"
        data-testid="pos-terminal"
      >
        <option value="">Choose a terminal…</option>
        {ctx.terminals
          .filter((t) => t.kind === 'pos' || t.id === ctx.terminal?.id)
          .map((t) => (
            <option key={t.id} value={t.id}>
              {t.code} · {t.name} · {drawerName(t.cash_drawer_id) ?? 'no cash drawer'}
            </option>
          ))}
      </Select>
    </div>
  );
}

/**
 * Cash taken in another shift (or before shifts) is given back here only when it is handed to the
 * customer from this drawer now: the server asks (`requires: cash_moves_now`) and the cashier
 * confirms, so the drawer's count expects it to be gone.
 */
export function useCashMovesNow(): {
  handle: (e: AppError, retry: () => Promise<void> | void) => boolean;
  sheet: ReactNode;
} {
  const [ask, setAsk] = useState<{ retry: () => Promise<void> | void } | null>(null);
  const online = useOnline();
  const handle = (e: AppError, retry: () => Promise<void> | void) => {
    if (e.code !== 'conflict' || hintOf(e).requires !== 'cash_moves_now') return false;
    setAsk({ retry });
    return true;
  };
  const sheet = (
    <Sheet open={!!ask} onClose={() => setAsk(null)} title="Cash from this drawer?">
      {ask && (
        <div className="d-stack" data-testid="cash-moves-now">
          <p className="d-muted">
            This cash was taken in another shift. Give it back in cash from this drawer now? The
            drawer’s count will expect it to be gone.
          </p>
          <div className="d-sheet__actions">
            <Button variant="ghost" onClick={() => setAsk(null)}>
              No
            </Button>
            <Button
              variant="primary"
              disabled={!online}
              onClick={() => {
                const r = ask.retry;
                setAsk(null);
                void r();
              }}
              data-testid="cash-moves-now-yes"
            >
              Yes, from this drawer
            </Button>
          </div>
        </div>
      )}
    </Sheet>
  );
  return { handle, sheet };
}
